import { type ComponentType, memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ClipStatus, ClipType } from '@air/api/types';
import { Skeleton } from '@air/component-skeleton';
import { ClipUtils } from '@air/utils-clips';
import { cn } from '@air/utils-tailwind';

import { EditModeInProgressOverlay } from '~/components/EditMode/components/EditModeInProgressOverlay';
import { useEditMode } from '~/components/EditMode/EditModeProvider';
import { useNoPreviewInVisualizer } from '~/hooks/useNoPreviewInVisualizer';
import {
  centralizedClipIsLoadingSelector,
  centralizedClipIsPDFTypeSelector,
  centralizedClipIsRiveSelector,
  centralizedClipPDFAssetSelector,
  centralizedClipStatusSelector,
  centralizedClipTypeSelector,
} from '~/store/centralizedClip/selectors';
import { FileVisualizer } from './components/FileVisualizer';
import { NoPreviewVisualizer } from './components/NoPreviewVisualizer';

enum VISUALIZER_TYPE {
  VIDEO = 'VIDEO',
  PDF = 'PDF',
  IMAGE = 'IMAGE',
  LOADER = 'LOADER',
  NOT_VIEWABLE = 'NOT_VIEWABLE',
  PROCESSING = 'PROCESSING',
  FILE = 'FILE',
  RIVE = 'RIVE',
}

export type VisualizerProps = {
  VideoVisualizer?: ComponentType;
  PDFVisualizer: ComponentType;
  ImageVisualizer: ComponentType;
  RiveVisualizer?: ComponentType;
  className?: string;
  /**
   * Per-asset remount key. Applied to time/page-based visualizers (video, PDF, Rive) so their
   * internal state resets when navigating between assets. It is intentionally NOT applied to the
   * image visualizer: remounting the image on every version/asset switch recreates the <img> and
   * reloads it from scratch, which causes a visible fit-to-view "zoom" flash (ONC-572). The image
   * visualizer resets its own per-asset state (zoom, source) without a remount.
   */
  perAssetResetKey?: string;
};

export const Visualizer = memo(
  ({
    VideoVisualizer,
    ImageVisualizer,
    PDFVisualizer,
    RiveVisualizer,
    className,
    perAssetResetKey,
  }: VisualizerProps) => {
    const isLoading = useSelector(centralizedClipIsLoadingSelector);
    const clipType = useSelector(centralizedClipTypeSelector);
    const clipStatus = useSelector(centralizedClipStatusSelector);
    const clipPDFAsset = useSelector(centralizedClipPDFAssetSelector);
    const isPdfRenderedFile = useSelector(centralizedClipIsPDFTypeSelector);
    const isRiveFile = useSelector(centralizedClipIsRiveSelector);
    const { noPreviewInVisualizer } = useNoPreviewInVisualizer();
    const { isEditModeEnabled, visualizerRef } = useEditMode();

    const visualizerType = useMemo<VISUALIZER_TYPE | null>(() => {
      if (isLoading) {
        return VISUALIZER_TYPE.LOADER;
      }

      if (!clipType) {
        return null;
      }

      if (isPdfRenderedFile) {
        if (clipPDFAsset) {
          return VISUALIZER_TYPE.PDF;
        } else {
          return VISUALIZER_TYPE.PROCESSING;
        }
      }

      // Check for nonMedia type before failed status (nonMedia takes precedence)
      if (clipType === ClipType.nonMedia) {
        return VISUALIZER_TYPE.FILE;
      }

      if (ClipUtils.isFailed(clipStatus)) {
        return VISUALIZER_TYPE.NOT_VIEWABLE;
      }

      /**
       * Show the processing state for all processing nonvideo assets
       * Show processing state for videos if video url isn't returned from websocket yet
       * When it is returned we know we have watchable video while the rest processes
       *  */
      if (noPreviewInVisualizer) {
        return VISUALIZER_TYPE.PROCESSING;
      }

      // Check for Rive files after processing check but BEFORE nonTranscodable check
      // since Rive files are marked as nonTranscodable but we can render them
      if (isRiveFile) {
        return VISUALIZER_TYPE.RIVE;
      }

      if (clipStatus === ClipStatus.nonTranscodable) {
        return VISUALIZER_TYPE.FILE;
      }

      if (clipType === ClipType.photo || clipType === ClipType.animated) {
        return VISUALIZER_TYPE.IMAGE;
      }

      return VISUALIZER_TYPE.VIDEO;
    }, [clipPDFAsset, clipStatus, clipType, isLoading, isPdfRenderedFile, isRiveFile, noPreviewInVisualizer]);

    const clipVisual = useMemo(() => {
      switch (visualizerType) {
        case VISUALIZER_TYPE.LOADER: {
          return <Skeleton width={300} />;
        }
        case VISUALIZER_TYPE.FILE: {
          return <FileVisualizer />;
        }
        case VISUALIZER_TYPE.NOT_VIEWABLE: {
          return <NoPreviewVisualizer />;
        }
        case VISUALIZER_TYPE.PDF: {
          return <PDFVisualizer key={perAssetResetKey} />;
        }
        case VISUALIZER_TYPE.PROCESSING: {
          return <NoPreviewVisualizer isProcessing={true} />;
        }
        case VISUALIZER_TYPE.IMAGE: {
          // No `key`: keep the image visualizer mounted across version/asset switches so the
          // <img> is reused (no reload flash). It resets zoom/source internally on clip change.
          return <ImageVisualizer />;
        }
        case VISUALIZER_TYPE.VIDEO: {
          return VideoVisualizer ? <VideoVisualizer key={perAssetResetKey} /> : null;
        }
        case VISUALIZER_TYPE.RIVE: {
          return RiveVisualizer ? <RiveVisualizer key={perAssetResetKey} /> : null;
        }
        default:
          return null;
      }
    }, [visualizerType, PDFVisualizer, ImageVisualizer, VideoVisualizer, RiveVisualizer, perAssetResetKey]);

    return (
      <div
        className={cn(
          'group/visualizer relative flex size-full min-h-0 items-center justify-center overflow-hidden',
          className,
        )}
        ref={visualizerRef}
      >
        <div className="flex size-full items-center justify-center">{clipVisual}</div>
        {isEditModeEnabled && <EditModeInProgressOverlay />}
      </div>
    );
  },
);

Visualizer.displayName = 'Visualizer';
